import { useState, useEffect } from 'react';
import { ProductivityQuestion } from '@/lib/api';

interface QuestionCardProps {
  question: ProductivityQuestion;
  answer: string;
  onChange: (answer: string) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function QuestionCard({
  question,
  answer,
  onChange,
  onNext,
  onPrevious,
  isFirst,
  isLast,
}: QuestionCardProps) {
  const [localAnswer, setLocalAnswer] = useState(answer);

  useEffect(() => {
    setLocalAnswer(answer);
  }, [answer]);

  const handleChange = (value: string) => {
    setLocalAnswer(value);
    onChange(value);
  };

  const isValid = localAnswer.trim().length > 0;

  return (
    <div className="question-card">
      <div className="mb-6">
        <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4">
          {question.text}
        </h3>

        {question.type === 'text' && (
          <textarea
            value={localAnswer}
            onChange={(e) => handleChange(e.target.value)}
            className="textarea"
            rows={4}
            placeholder="Type your answer here..."
            autoFocus
          />
        )}

        {question.type === 'choice' && question.options && (
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <label
                key={index}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  localAnswer === option
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-primary-300'
                }`}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={localAnswer === option}
                  onChange={(e) => handleChange(e.target.value)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                    localAnswer === option
                      ? 'border-primary-600 bg-primary-600'
                      : 'border-gray-400'
                  }`}
                >
                  {localAnswer === option && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className="text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'multiple' && question.options && (
          <div className="space-y-3">
            {question.options.map((option, index) => {
              const selectedOptions = localAnswer ? localAnswer.split('|||') : [];
              const isSelected = selectedOptions.includes(option);

              return (
                <label
                  key={index}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-primary-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={question.id}
                    value={option}
                    checked={isSelected}
                    onChange={(e) => {
                      const currentOptions = localAnswer ? localAnswer.split('|||') : [];
                      let newOptions: string[];

                      if (e.target.checked) {
                        newOptions = [...currentOptions, option];
                      } else {
                        newOptions = currentOptions.filter(o => o !== option);
                      }

                      handleChange(newOptions.join('|||'));
                    }}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                      isSelected
                        ? 'border-primary-600 bg-primary-600'
                        : 'border-gray-400'
                    }`}
                  >
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-gray-900">{option}</span>
                </label>
              );
            })}
            <p className="text-sm text-gray-500 mt-2">Select all that apply</p>
          </div>
        )}

        {question.type === 'scale' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  onClick={() => handleChange(num.toString())}
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full font-semibold transition-all ${
                    localAnswer === num.toString()
                      ? 'bg-primary-600 text-white scale-110 shadow-lg'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 px-2">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <button
          onClick={onPrevious}
          disabled={isFirst}
          className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLast ? 'Continue' : 'Next'}
        </button>
      </div>
    </div>
  );
}
